import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Modal, Button, ListGroup, Form, Spinner } from "react-bootstrap";
import { savOperations } from "state/ducks/store/sav";

/**
 * Guide rapide : l'utilisateur choisit une phrase ("Je veux ...") et un
 * tutoriel interactif démarre, mettant en évidence les éléments de l'écran
 * dans l'ordre des actions à faire (cf TutorialOverlay).
 * target = sélecteur [data-tour="..."] posé dans Intendance.js (null = bulle centrée).
 */
// Ne clique "Voir PDR" que si la tournée n'est pas déjà dépliée
const whenNotExpanded = (element) => element.textContent.includes("Voir");

export const GUIDES = [
  {
    title: "Je veux géolocaliser un livreur et savoir le retard qu'il aura au prochain point",
    steps: [
      { target: '[data-tour="address-search"]', action: "focus", text: "Tapez ici l'adresse où se trouve le livreur puis choisissez une suggestion — ou appuyez sur Entrée pour valider la première. Un marqueur 🚲 se place sur la carte." },
      { target: '[data-tour="bike-estimate"]', action: "click", text: "Une fois l'adresse localisée, cliquez « Faire une estimation d'arrivée sur le point » dans le bandeau vert au-dessus de la carte." },
      { target: null, text: "La popup liste tous les points de retrait du jour : cherchez puis cliquez le prochain point du livreur. Sa tournée s'affiche automatiquement sur la carte." },
      { target: '[data-tour="bike-banner"]', text: "Le bandeau au-dessus de la carte affiche le temps de trajet, l'itinéraire se dessine, et un badge indique « Dans les temps » (vert) ou « En retard de X min » (rouge) par rapport à l'heure prévue au point." },
      { target: '[data-tour="routing-mode"]', text: "Le mode 🚲 Vélo / 🚗 Voiture s'applique à tout : tracés des tournées, position théorique du livreur et estimations. Basculez ici si le livreur n'est pas à vélo — tout est recalculé." },
    ],
  },
  {
    title: "Je veux prendre des notes sur une tournée pour mon suivi",
    steps: [
      { target: '[data-tour="tours-list"]', text: "Repérez la tournée dans la liste (utilisez les filtres au-dessus si besoin)." },
      { target: '[data-tour="notes-tour"]', action: "click", text: "Cliquez « 🗒 Notes de la tournée » : saisissez votre note pour le jour affiché puis « Ajouter la note ». L'historique complet s'affiche en dessous, et l'onglet « Notes des points de la tournée » regroupe les notes du jour de ses points." },
    ],
  },
  {
    title: "Je veux prendre des notes sur un point en particulier pour mon suivi",
    steps: [
      { target: '[data-tour="voir-pdr"]', action: "click", actionWhen: whenNotExpanded, text: "Dépliez la tournée du point avec « Voir PDR »." },
      { target: '[data-tour="tours-list"]', text: "Cliquez l'icône 🗒 en bout de ligne du point concerné : la modale affiche les notes de ce point pour le jour affiché et permet d'en ajouter une." },
    ],
  },
  {
    title: "Un client m'appelle pour avoir des informations, je veux savoir sur quel point il a commandé",
    steps: [
      { target: '[data-tour="customer-search"]', action: "focus", text: "Tapez le nom, prénom ou téléphone du client et sélectionnez-le dans les suggestions — ou appuyez sur Entrée pour prendre le premier." },
      { target: '[data-tour="customer-card"]', text: "Sa fiche s'affiche au-dessus de la carte avec ses commandes du jour : point de retrait, code, tournée et horaire. « Voir sur la carte » zoome sur le point, « 🗒 Annoter » laisse une note sur ce point." },
    ],
  },
  {
    title: "Je veux attribuer mes livreurs et afficher le récapitulatif",
    steps: [
      { target: '[data-tour="recap"]', action: "click", text: "Cliquez « Attribuer livreur » : la popup liste toutes les tournées du jour (tournée, heure de départ, établissement, livreur, téléphone)." },
      { target: null, text: "Choisissez le livreur de chaque tournée (tapez quelques lettres pour filtrer). L'affectation est enregistrée immédiatement pour la date affichée ; la croix la retire. Les tournées sans livreur ressortent en orange, et la colonne de gauche affiche le livreur affecté sous chaque tournée." },
      { target: null, text: "En haut de la popup, la section « 🏠 Intendance du jour » affecte la personne à l'intendance : une par établissement et/ou une globale qui couvre tout." },
    ],
  },
  {
    title: "Je veux contacter un ou plusieurs points pour envoyer un SMS",
    steps: [
      { target: '[data-tour="voir-pdr"]', action: "click", actionWhen: whenNotExpanded, text: "Affichez les tournées concernées avec « Voir PDR »." },
      { target: '[data-tour="tours-list"]', text: "Sélectionnez les points via les cases de la liste (« Sélectionner tous les points ») ou en cliquant les marqueurs sur la carte." },
      { target: '[data-tour="sms"]', action: "click", text: "Cliquez « Envoyer un SMS ». Vous pourrez aussi ajuster les points ciblés directement dans la popup." },
      { target: null, text: "Dans la popup : complétez les « A RENSEIGNER » du message pré-rempli puis validez. Le numéro d'intendance de la charrette de chaque client est ajouté automatiquement à la fin du SMS, et un bandeau rouge vous prévient si des clients ont déjà été notifiés aujourd'hui." },
    ],
  },
  {
    title: "Je veux connaître le retard du livreur et ensuite prévenir mes clients par SMS",
    steps: [
      { target: '[data-tour="address-search"]', action: "focus", text: "Localisez le livreur : tapez ici l'adresse où il se trouve et choisissez une suggestion — ou appuyez sur Entrée pour valider la première." },
      { target: '[data-tour="bike-estimate"]', action: "click", text: "Cliquez « Faire une estimation d'arrivée sur le point », puis choisissez le prochain point du livreur dans la liste : le retard estimé s'affiche." },
      { target: '[data-tour="bike-banner"]', text: "Dans le bandeau d'estimation, cliquez « Envoyer un SMS » : le point est sélectionné automatiquement et le message est pré-rempli avec le retard estimé et le nouveau créneau. Vérifiez puis validez." },
    ],
  },
  {
    title: "Je veux voir si j'ai déjà envoyé un SMS aujourd'hui",
    steps: [
      { target: '[data-tour="sms"]', action: "click", text: "Ouvrez « Envoyer un SMS » — même sans aucun point sélectionné." },
      { target: null, text: "En bas de la popup, la section « Envois du jour (tous points confondus) » liste tous les SMS et emails du jour : client, heure, point et contenu envoyé." },
      { target: null, text: "L'historique est aussi accessible depuis l'onglet « Message SMS ou Email » via le bouton « 📨 Historique des envois », avec le choix du jour." },
    ],
  },
  {
    title: "Je veux marquer un point comme livré (ou voir ce qui a déjà été livré)",
    steps: [
      { target: '[data-tour="voir-pdr"]', action: "click", actionWhen: whenNotExpanded, text: "Dépliez la tournée concernée avec « Voir PDR » : la liste de ses points s'affiche." },
      { target: '[data-tour="tours-list"]', text: "Cliquez la coche ronde en bout de ligne du point : elle passe au vert = livré pour le jour affiché. Re-cliquer annule. Sur la carte, le marqueur du point porte alors une coche verte." },
      { target: '[data-tour="map-area"]', text: "L'état est partagé avec l'app livraison : un point coché par le livreur depuis sa tournée apparaît ici automatiquement (sous ~15 secondes, sans recharger), et inversement. Tout est par jour — changer de date affiche les livraisons de ce jour-là." },
    ],
  },
];

const GuideModal = (props) => {

  const { show, onClose, onStartTutorial } = props;

  const dispatch = useDispatch();

  // Demande d'ajout d'une question au tutoriel (envoyée sur Connecteam)
  const [requestMode, setRequestMode] = useState(false);
  const [requestText, setRequestText] = useState("");
  const [isSending, setIsSending]     = useState(false);
  const [feedback, setFeedback]       = useState(null); // {variant, text}

  const handleClose = () => {
    setRequestMode(false);
    setRequestText("");
    setFeedback(null);
    onClose();
  };

  const sendRequest = () => {
    if (!requestText.trim()) {
      return;
    }
    setIsSending(true);
    setFeedback(null);
    const message = `💡 Demande d'ajout au tutoriel Intendance charrette :\n${requestText.trim()}`;
    dispatch(savOperations.sendIntendanceRecapConnecteam(message))
      .then((result) => {
        const data = result?.data?.result;
        if (data && data.success) {
          setFeedback({ variant: "success", text: "Demande envoyée sur Connecteam, merci !" });
          setRequestText("");
        } else {
          setFeedback({ variant: "danger", text: `Échec de l'envoi : ${(data && data.message) || "erreur inconnue"}` });
        }
      })
      .catch(() => {
        setFeedback({ variant: "danger", text: "Échec de l'envoi sur Connecteam." });
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {requestMode ? "Demander une nouvelle question" : "Tutoriel - Que voulez-vous faire ?"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!requestMode ? (
          <>
            <p className="text-muted">
              Choisissez une action : un tutoriel interactif vous montre où cliquer, étape par étape.
            </p>
            <ListGroup>
              {GUIDES.map((guide, index) => (
                <ListGroup.Item
                  action
                  key={index}
                  onClick={() => onStartTutorial(guide)}
                >
                  {guide.title}
                </ListGroup.Item>
              ))}
              <ListGroup.Item
                action
                variant="light"
                onClick={() => setRequestMode(true)}
              >
                <span role="img" aria-label="idée">💡</span>{" "}
                <strong>Il vous manque une question ? Faites la demande</strong>
              </ListGroup.Item>
            </ListGroup>
          </>
        ) : (
          <>
            <Form.Group>
              <Form.Label>
                Décrivez ce que vous aimeriez pouvoir faire ou apprendre :
                la demande sera envoyée sur Connecteam.
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={requestText}
                onChange={(e) => setRequestText(e.target.value)}
                placeholder="Ex : Je veux savoir comment..."
              />
            </Form.Group>
            {feedback && (
              <p className={`font-weight-bold ${feedback.variant === "success" ? "text-success" : "text-danger"}`}>
                {feedback.variant === "success"
                  ? <span role="img" aria-label="succès">✅</span>
                  : <span role="img" aria-label="erreur">❌</span>}{" "}
                {feedback.text}
              </p>
            )}
            <Button
              variant="link"
              className="p-0"
              onClick={() => {
                setRequestMode(false);
                setFeedback(null);
              }}
            >
              ← Retour aux questions
            </Button>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          Fermer
        </Button>
        {requestMode && (
          <Button
            variant="primary"
            disabled={!requestText.trim() || isSending}
            onClick={sendRequest}
          >
            {isSending ? (
              <>
                <Spinner animation="border" size="sm" className="mr-2" />
                Envoi en cours...
              </>
            ) : (
              "Envoyer la demande"
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

GuideModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onStartTutorial: PropTypes.func.isRequired,
};

GuideModal.defaultProps = {
  show: false,
};

export default GuideModal;
